import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode.react";
import qs from "qs";
import { RightOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { ListItem, Modal } from "../common/index.jsx";
import buyDealAPI from "@/service/buy-deal";
import { LoadingModal } from "@/components/common";
import "./index.less";
import { message } from "antd";
import IconPay from '../images/order_pay.svg'

/**
 * 支付选择器
 * @param {*} props
 * @returns
 */

export function PaySelector({
  payRef,
  paymentInfo,
  paymentMethods,
  payMethod,
  setPayMethod,
  orderInfo,
  payCallback,
  baseUrl,
}) {
  const { t } = useTranslation();
  const [showPayPopup, setPayPopup] = useState(false);
  const [showQrocde, setShowQrocde] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("loading");
  const [prePayInfo, setPrePayInfo] = useState({});
  const timtRef = useRef(null);
  const getSuccessUrl = () => {
    let query = qs.stringify(
      {
        payment: true,
        private_token: window.private_token,
        order_id: orderInfo.uuid,
      },
      { delimiter: "-" }
    );
    return baseUrl + query;
  };
  getSuccessUrl();
  // 关闭二维码支付页面，需要立即获取支付结果
  const cancelWechatPay = () => {
    clearTimeout(timtRef.current);
    setShowQrocde(false);
    setLoading(true);
    setLoadingStatus("loading");
    buyDealAPI.getPayResult(orderInfo.payment.uuid).then(({ status }) => {
      if (status === "paid") {
        onPaySuccess();
      } else {
        setLoading(false);
        payCallback("failed");
      }
    });
  };

  // 支付成功
  const onPaySuccess = () => {
    setLoading(false);
    setShowQrocde(false);
    clearTimeout(timtRef.current);
    setLoadingStatus("success");
    payCallback("successed"); // 支付成功，跳转到订单详情
  };
  // 轮询支付结果
  const checkPayStatus = () => {
    buyDealAPI.getPayResult(orderInfo.payment.uuid).then(({ status }) => {
      if (status === "pending") {
        timtRef.current = setTimeout(checkPayStatus, 2000);
      } else if (status === "paid") {
        onPaySuccess();
      }
    });
  };

  // 暴露给父组件的方法
  useImperativeHandle(payRef, () => ({
    callPay: (uuid) => {
      setLoading(true);
      setLoadingStatus("loading");
      // 获取支付凭证
      buyDealAPI
        .prePayment({
          channel: payMethod.keyword,
          uuid: uuid || orderInfo.payment?.uuid,
          success_url: getSuccessUrl(),
        })
        .then((res) => {
          setPrePayInfo(res);
          checkPayStatus();
          // 二维码支付
          if (["wx_pub_qr", "alipay_qr"].includes(res.channel)) {
            setTimeout(() => {
              setLoading(false);
            }, 0);
            setShowQrocde(true);
          } else {
            // ping++支付
            buyDealAPI
              .callPayment({
                prePayment: res,
              })
              .then((res) => {
                setLoadingStatus(res === "success" ? "success" : "failed");
              })
              .catch((err) => {
                console.error(err);
                setLoadingStatus("failed");
                setTimeout(() => {
                  setLoading(false);
                  payCallback("failed");
                }, 2000);
                clearTimeout(timtRef.current);
              });
          }
        })
        .catch((err) => {
          console.error(err);
          setLoadingStatus("failed");
          setTimeout(() => {
            setLoading(false);
            payCallback("failed");
          }, 2000);
          clearTimeout(timtRef.current);
          message.error(err?.data?.full_messages?.[0] || err.message);
        });
    },
  }));

  useEffect(() => {
    return () => {
      clearTimeout(timtRef.current);
    };
  }, []);

  return (
    <React.Fragment>
      {/* 支付方式选择器 */}
      <ListItem
        onClick={() => setPayPopup(true)}
        icon={IconPay}
        title={t("PAYMENT.Payment method")}
        value={
          <div
            className="buydeal-pay-item"
            style={payMethod.name ? { background: "#fff" } : {}}
          >
            <span>
              {payMethod.name ? (
                <img src={payMethod.icon} style={{ height: 24 }} />
              ) : (
                t("walkin.Please select")
              )}
            </span>
            {!payMethod.name && (
              <RightOutlined style={{ fontSize: "12px", color: "#fff" }} />
            )}
          </div>
        }
      ></ListItem>
      {/* 预付金额 */}
      <PrePayment title={paymentInfo?.amount_type_humanize}></PrePayment>
      {/* 预付明细 */}
      <ListItem
        title={
          <span style={{ fontWeight: 400, fontSize: 12 }}>
            {paymentInfo.payment_mode_humanize}
          </span>
        }
        value={`${paymentInfo.currency + paymentInfo.item_price} * ${
          paymentInfo.item_count
        }`}
      ></ListItem>

      <LoadingModal
        visible={loading}
        loading={loadingStatus}
        setVisible={setLoading}
        onComplete={onPaySuccess}
        term={false}
      ></LoadingModal>

      {showPayPopup && (
        <PayPopup
          show={showPayPopup}
          paymentMethods={paymentMethods}
          setPayPopup={setPayPopup}
          setPayMethod={setPayMethod}
        ></PayPopup>
      )}
      {showQrocde && (
        <QrocdePopup
          prePayInfo={prePayInfo}
          cancelWechatPay={cancelWechatPay}
        ></QrocdePopup>
      )}
    </React.Fragment>
  );
}

/**
 * 预付款提示
 * @param {*} param0
 * @returns
 */
const PrePayment = ({ title }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  return (
    <section>
      <ListItem
        onClick={() => setVisible(true)}
        value={
          <span>
            {title}
            <QuestionCircleOutlined
              style={{
                fontSize: 16,
                marginLeft: 8,
                verticalAlign: -3,
                color: "#333",
              }}
            />
          </span>
        }
      ></ListItem>
      {visible && (
        <Modal title={title} show={visible} onClose={() => setVisible(false)}>
          <p style={{ textAlign: "center" }}>{t("walkin.pre_payment_desc")}</p>
        </Modal>
      )}
    </section>
  );
};

/**
 * 弹窗-选择支付方式
 * @param {*} props
 * @returns
 */
export function PayPopup({ setPayPopup, paymentMethods, setPayMethod }) {
  const { t } = useTranslation();
  const [animate, setAnimate] = useState("in");

  function onBack() {
    setAnimate("out");
    setTimeout(() => {
      setPayPopup(false);
    }, 250);
  }

  function onSelect(keyword) {
    setPayMethod(keyword);
    onBack();
  }

  return (
    <div className="buydeal-popup animate" data-animate={animate}>
      <div className="buydeal-popup-header">
        <div className="header-left" onClick={onBack}>
          <i className="icon icon-arrow-left"></i>
        </div>
        <strong>{t("PAYMENT.Payment method")}</strong>
        <span className="header-right"></span>
      </div>
      <div className="buydeal-popup-line"></div>
      <div className="buydeal-popup-body">
        <div className="buydeal-popup-paylist">
          {paymentMethods.map((item, index) => {
            return (
              <div
                className="buydeal-popup-payitem"
                key={index}
                onClick={() => onSelect(item)}
              >
                <img src={item.icon} alt="" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * 弹窗-二维码支付
 * @param {*} props
 * @returns
 */
export function QrocdePopup({ prePayInfo, cancelWechatPay }) {
  const { t } = useTranslation();
  const [animate, setAnimate] = useState("in");
  const qrcode = useMemo(() => {
    const { channel, credential = {} } = prePayInfo;
    switch (channel) {
      case "alipay_qr":
        return credential.alipay_qr;
        break;
      case "wx_pub_qr":
        return credential.wx_pub_qr;
        break;
    }
  }, [prePayInfo]);

  function onBack() {
    setAnimate("out");
    setTimeout(() => {
      cancelWechatPay();
    }, 250);
  }

  return (
    <div className="buydeal-popup animate" data-animate={animate}>
      <div className="buydeal-popup-header">
        <div className="header-left" onClick={onBack}>
          <i className="icon icon-arrow-left"></i>
        </div>
        <strong>{t("PAYMENT.Payment method")}</strong>
        <span className="header-right"></span>
      </div>
      <div className="buydeal-popup-line"></div>
      <div className="buydeal-popup-body">
        <div className="step-payment">
          <h2>{t("PAYMENT.PAY WITH WECHAT")}</h2>
          <div className="tip">{t("PAYMENT.qrcode tip")}</div>

          <div className="qrcode">
            {qrcode ? <QRCode size={145} value={qrcode} /> : null}
          </div>
        </div>
      </div>
      <div className="buydeal-popup-footer">
        <div className="buydeal-popup-btn" onClick={onBack}>
          {t("CANCEL")}
        </div>
      </div>
    </div>
  );
}
